import { useCallback, useEffect, useMemo, useState } from 'react'

/* ─── Shared analytics filter state (MS-700 AC10–12, spec §5a) ───

   Filters, sort and page live in **sessionStorage, keyed per connection**. Three
   properties fall out of that choice and each is a requirement, not an accident:

   * **Per connection.** The key carries the connection, so nothing crosses the
     boundary: Core's filters never appear on Payments' screen.
   * **Session, not saved views.** Closing the tab discards them. These are a
     working position, not a named artefact the admin has to manage.
   * **Not the URL.** The tab is in the URL and has exactly one writer
     (`McpAnalyticsDetail`); putting a dozen filter values there too would mean a
     second writer racing the first on every keystroke.

   The period is stored as a *key* ("last12m") rather than as resolved dates, so a
   session restored the next morning still means "the last 12 months" and not "the
   12 months ending yesterday". A custom range stores its explicit dates instead. */

export const PERIOD_KEYS = ['last7d', 'last30d', 'last90d', 'last12m'] as const
export type PeriodKey = (typeof PERIOD_KEYS)[number]

export const PERIOD_DAYS: Record<PeriodKey, number> = {
  last7d: 7,
  last30d: 30,
  last90d: 90,
  last12m: 365,
}

/** The prototype's `mountDateRange({ defaultPeriod: { label: 'Last 12 months' } })`. */
export const DEFAULT_PERIOD: PeriodKey = 'last12m'

/** Day granularity shows a trailing ~3-month window regardless of the selected
 *  period (MS-701 AC2): 365 daily columns is not a chart. */
export const DAY_WINDOW_DAYS = 91

export type Granularity = 'day' | 'month' | 'quarter' | 'year'

export interface AnalyticsFilterState {
  /** Emails. The API resolves them to subs; the picker shows names. */
  users: string[]
  channels: string[]
  invocationModes: string[]
  period: PeriodKey | 'custom'
  /** ISO dates (yyyy-mm-dd), only meaningful when `period === 'custom'`. */
  customStart: string | null
  customEnd: string | null
}

export const EMPTY_FILTERS: AnalyticsFilterState = {
  users: [],
  channels: [],
  invocationModes: [],
  period: DEFAULT_PERIOD,
  customStart: null,
  customEnd: null,
}

function storageKey(connectionKey: string): string {
  return `mcp-analytics:filters:${connectionKey}`
}

function readStored(connectionKey: string): AnalyticsFilterState {
  try {
    const raw = sessionStorage.getItem(storageKey(connectionKey))
    if (!raw) return EMPTY_FILTERS
    const parsed = JSON.parse(raw) as Partial<AnalyticsFilterState>
    // Merged onto the defaults rather than trusted wholesale: a stored shape from
    // an older build must not leave a field undefined for the rest of the session.
    return { ...EMPTY_FILTERS, ...parsed }
  } catch {
    // A corrupt or unavailable store is not worth failing the page over — the
    // filters simply start empty.
    return EMPTY_FILTERS
  }
}

/**
 * `[from, to]` as ISO instants for the API.
 *
 * Both ends are absolute instants derived at call time, never "now minus N days"
 * evaluated inside the query, so the chart and the table below it are guaranteed
 * to be asking about the same window.
 */
export function resolvePeriod(
  filters: AnalyticsFilterState,
  now: Date = new Date(),
): { from: string; to: string } {
  if (filters.period === 'custom' && filters.customStart && filters.customEnd) {
    const start = new Date(`${filters.customStart}T00:00:00`)
    const end = new Date(`${filters.customEnd}T23:59:59.999`)
    return { from: start.toISOString(), to: end.toISOString() }
  }
  const days = PERIOD_DAYS[(filters.period as PeriodKey) in PERIOD_DAYS ? (filters.period as PeriodKey) : DEFAULT_PERIOD]
  const from = new Date(now)
  from.setDate(from.getDate() - days)
  return { from: from.toISOString(), to: now.toISOString() }
}

/**
 * The chart's window, which is narrower than the table's at day granularity.
 *
 * That asymmetry is the spec's intent (§8, MS-701 AC2) and must not be "fixed" by
 * widening the chart. It does mean a bar click has to send the clicked bucket's
 * explicit bounds rather than assume the two share a window.
 */
export function resolveChartPeriod(
  filters: AnalyticsFilterState,
  granularity: Granularity,
  now: Date = new Date(),
): { from: string; to: string } {
  const full = resolvePeriod(filters, now)
  if (granularity !== 'day') return full
  const trailing = new Date(full.to)
  trailing.setDate(trailing.getDate() - DAY_WINDOW_DAYS)
  const from = new Date(Math.max(trailing.getTime(), new Date(full.from).getTime()))
  return { from: from.toISOString(), to: full.to }
}

/** The viewer's IANA zone, sent so the server truncates buckets at *their* local
 *  midnight. Falls back to UTC in the (rare) environment with no resolved zone. */
export function viewerTimeZone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'
}

export function useAnalyticsFilters(connectionKey: string) {
  const [filters, setFilters] = useState<AnalyticsFilterState>(() => readStored(connectionKey))

  // Re-read on a connection change rather than carrying the previous one's values
  // across — that would be exactly the leak the per-connection key exists to stop.
  useEffect(() => {
    setFilters(readStored(connectionKey))
  }, [connectionKey])

  useEffect(() => {
    try {
      sessionStorage.setItem(storageKey(connectionKey), JSON.stringify(filters))
    } catch {
      // Storage full or blocked: the filters still work for this render, they just
      // do not survive a reload. Not worth surfacing.
    }
  }, [connectionKey, filters])

  const update = useCallback((patch: Partial<AnalyticsFilterState>) => {
    setFilters(prev => ({ ...prev, ...patch }))
  }, [])

  /** Every value the queries key on. Memoised so a re-render does not restart them. */
  const queryKey = useMemo(
    () => [
      connectionKey,
      filters.users.join(','),
      filters.channels.join(','),
      filters.invocationModes.join(','),
      filters.period,
      filters.customStart,
      filters.customEnd,
    ],
    [connectionKey, filters],
  )

  return { filters, update, queryKey }
}
